import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { initRenderingOptions, type GlassesRenderingOptions } from "types"
import { GlassesDigitizationCard } from "./GlassesDigitizationCard"
import { FixedHeaderLayout } from "components/FixedHeaderLayout/FixedHeaderLayout"
import { GlassesMetadataCard } from "./GlassesMetadataCard"
import { GlassesPreviewCard } from "./GlassesPreviewCard"
import { useGlassesEditor } from "./useGlassesEditor"
import { TintMark } from "@/components/TintMark/TintMark"
import UnsavedChangesDialog from "components/UnsavedChangesDialog"

export const GlassesEditorPage = () => {
  const { t } = useTranslation()
  const [showValidation, setShowValidation] = useState(false)
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)
  const {
    addSourceImages,
    canPublishProduct,
    canSave,
    canStartDigitization,
    closeEditor,
    discardChanges,
    editor,
    glassesTransformMode,
    hasUnsavedChanges,
    hydrateRenderingOptionsFromViewer,
    isExistingProduct,
    isLoading,
    isParsingTryOn,
    isReady,
    isSaveDisabled,
    isSaving,
    isStarting,
    previewImage,
    previewProduct,
    previewResetToken,
    removeSourceImage,
    saveChanges,
    saveActionLabel,
    setCover,
    setField,
    setPreviewImage,
    setRenderingOptions,
    setVisible,
    startFeedbackMessage,
    startRestrictionMessage,
    startDigitization,
  } = useGlassesEditor()

  const isDigitizationInProgress =
    isStarting ||
    editor.digitization.status === "queued" ||
    editor.digitization.status === "processing"
  const renderingOptions = previewProduct.renderingOptions ?? initRenderingOptions

  // Auto-enable Try-on once the AR model becomes ready and the product is publishable.
  // Fires only on the ready transition, so a manually-disabled or already-saved product
  // is never silently re-enabled.
  const autoEnableTryOnRef = useRef(false)
  const prevDigitizationStatusRef = useRef(editor.digitization.status)
  useEffect(() => {
    const prevStatus = prevDigitizationStatusRef.current
    const status = editor.digitization.status
    prevDigitizationStatusRef.current = status
    if (status === "ready" && prevStatus !== "ready") autoEnableTryOnRef.current = true
    if (status !== "ready") autoEnableTryOnRef.current = false
    if (autoEnableTryOnRef.current && canPublishProduct && !editor.visible) {
      autoEnableTryOnRef.current = false
      setVisible(true)
    }
  }, [editor.digitization.status, canPublishProduct, editor.visible, setVisible])

  if (isLoading)
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center p-6"
        data-testid="glasses-editor-loading"
      >
        <TintMark className="size-10 animate-pulse rounded-xl" />
      </div>
    )

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowLeaveDialog(true)
      return
    }
    closeEditor()
  }

  return (
    <div data-testid="glasses-editor-page">
      <FixedHeaderLayout
        title="Glasses"
        onBack={handleBack}
        onFirstButtonClicked={discardChanges}
        onSecondButtonClicked={() => void saveChanges()}
        onSecondButtonLoading={isSaving}
        isFirstButtonDisabled={isExistingProduct && (!hasUnsavedChanges || isSaving)}
        secondButtonDisabled={isSaveDisabled}
        firstButtonText={isExistingProduct ? t("productPage.discard") : undefined}
        secondButtonText={saveActionLabel}
        validateSecondButton={() => {
          if (!canSave) {
            setShowValidation(true)
            return false
          }
          return true
        }}
      />
      <div className="mx-auto max-w-[1440px] space-y-3 p-6" data-testid="glasses-editor-content">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,800px)_minmax(420px,1fr)]">
          <div className="space-y-3" data-testid="glasses-editor-form">
            <GlassesMetadataCard
              coverPreviewUrl={editor.coverPreviewUrl}
              isLocked={isDigitizationInProgress}
              name={editor.name}
              onCoverChange={setCover}
              onNameChange={(value) => setField("name", value)}
              showValidation={showValidation}
            />
            <GlassesDigitizationCard
              canStartDigitization={canStartDigitization}
              digitization={editor.digitization}
              isStarting={isStarting}
              needsEffect={
                showValidation &&
                !editor.effectUrl?.trim() &&
                editor.digitization.status === "draft"
              }
              onAddSourceImages={addSourceImages}
              onRemoveSourceImage={removeSourceImage}
              onStartDigitization={() => void startDigitization()}
              onVisibleChange={setVisible}
              showInStoreDisabled={!canPublishProduct}
              visible={editor.visible}
              feedbackMessage={startFeedbackMessage}
              startRestrictionMessage={startRestrictionMessage}
              sourceImages={editor.sourceImages}
            />
          </div>

          <GlassesPreviewCard
            key={previewResetToken}
            glassesTransformMode={glassesTransformMode}
            isParsingTryOn={isParsingTryOn}
            isReady={isReady}
            onGlassesTransformsResolved={hydrateRenderingOptionsFromViewer}
            onPositionXChange={(value) => {
              const nextOptions: GlassesRenderingOptions = {
                ...renderingOptions,
                positionAccuracy: {
                  ...renderingOptions.positionAccuracy,
                  x: value,
                },
              }
              setRenderingOptions(nextOptions)
            }}
            onPositionYChange={(value) => {
              const nextOptions: GlassesRenderingOptions = {
                ...renderingOptions,
                positionAccuracy: {
                  ...renderingOptions.positionAccuracy,
                  y: value,
                },
              }
              setRenderingOptions(nextOptions)
            }}
            onScaleChange={(value) => {
              const nextOptions: GlassesRenderingOptions = {
                ...renderingOptions,
                scale: { x: value, y: value, z: value },
              }
              setRenderingOptions(nextOptions)
            }}
            onPreviewImageChange={setPreviewImage}
            previewProduct={previewProduct}
            previewImage={previewImage}
          />
        </div>
      </div>
      <UnsavedChangesDialog
        isOpen={showLeaveDialog}
        onClose={() => setShowLeaveDialog(false)}
        onLeave={() => {
          setShowLeaveDialog(false)
          closeEditor()
        }}
      />
    </div>
  )
}
